/**
 * SkillSystem.ts — Skill tree allocation, prerequisite checking, and bonus calculation.
 *
 * Points share the same pool as stat points (2 per level, 30 total at level 16).
 * Players choose between raw stat investment and skill investment.
 */

import type { GameState } from '@core/engine/GameState';
import type { ClassId, StatName } from '@game/data/types';
import {
  getSkill,
  getSkillsForClass,
  type SkillDefinition,
  type SkillEffectType,
} from '@game/data/skills';

// ─── Public API ─────────────────────────────────────────────────

export class SkillSystem {
  /**
   * Check if a skill can be allocated (prereqs met, has points, under max level).
   */
  canAllocate(skillId: string, state: GameState): boolean {
    const skill = getSkill(skillId);
    if (!skill) return false;

    // Must be correct class
    if (skill.classId !== state.character.classId) return false;

    // Must have unspent stat points
    if (state.character.unspentStatPoints <= 0) return false;

    // Check current level against max
    const currentLevel = state.skillLevels.get(skillId) ?? 0;
    if (currentLevel >= skill.maxLevel) return false;

    // Check prerequisites
    return this.meetsPrereqs(skill, state);
  }

  /**
   * Allocate one point into a skill. Returns false if not allowed.
   */
  allocate(skillId: string, state: GameState): boolean {
    if (!this.canAllocate(skillId, state)) return false;

    const current = state.skillLevels.get(skillId) ?? 0;
    state.skillLevels.set(skillId, current + 1);
    state.character.unspentStatPoints--;

    return true;
  }

  /**
   * Check if all prerequisites are met for a skill.
   */
  meetsPrereqs(skill: SkillDefinition, state: GameState): boolean {
    for (const prereq of skill.prereqs) {
      const prereqLevel = state.skillLevels.get(prereq.skillId) ?? 0;
      if (prereqLevel < prereq.minLevel) return false;
    }
    return true;
  }

  /**
   * Get the total bonus of a given effect type from all allocated skills.
   */
  getTotalBonus(effectType: SkillEffectType, state: GameState): number {
    let total = 0;
    const classSkills = getSkillsForClass(state.character.classId);

    for (const skill of classSkills) {
      const level = state.skillLevels.get(skill.id) ?? 0;
      if (level === 0) continue;

      for (const effect of skill.effects) {
        if (effect.type === effectType) {
          total += effect.valuePerLevel * level;
        }
      }
    }

    return total;
  }

  /**
   * Get the stat check bonus for a specific stat from skills.
   */
  getStatCheckBonus(stat: StatName, state: GameState): number {
    let total = 0;
    const classSkills = getSkillsForClass(state.character.classId);

    for (const skill of classSkills) {
      const level = state.skillLevels.get(skill.id) ?? 0;
      if (level === 0) continue;

      for (const effect of skill.effects) {
        if (effect.type === 'stat_check_bonus' && effect.stat === stat) {
          total += effect.valuePerLevel * level;
        }
      }
    }

    return total;
  }

  /**
   * Get total points invested in skills.
   */
  getPointsInvested(state: GameState): number {
    let total = 0;
    for (const [, level] of state.skillLevels) {
      total += level;
    }
    return total;
  }

  /**
   * Get points invested in a specific branch.
   */
  getPointsInBranch(classId: ClassId, branch: string, state: GameState): number {
    let total = 0;
    const classSkills = getSkillsForClass(classId);

    for (const skill of classSkills) {
      if (skill.branch !== branch) continue;
      const level = state.skillLevels.get(skill.id) ?? 0;
      total += level;
    }

    return total;
  }
}
